import type { WebAuthnRegisterOptions, WebAuthnLoginOptions } from '@/types/entities'

/**
 * Enterprise WebAuthn utility functions with YubiKey and hardware security key support
 * Implements FIDO2/WebAuthn best practices for enterprise environments
 */

export interface WebAuthnError extends Error {
    code: string
    name: string
}

export class WebAuthnNotSupportedError extends Error {
    constructor() {
        super('WebAuthn is not supported in this browser')
        this.name = 'WebAuthnNotSupportedError'
    }
}

export class WebAuthnCancelledError extends Error {
    constructor() {
        super('WebAuthn operation was cancelled by user')
        this.name = 'WebAuthnCancelledError'
    }
}

export class WebAuthnTimeoutError extends Error {
    constructor() {
        super('WebAuthn operation timed out')
        this.name = 'WebAuthnTimeoutError'
    }
}

export interface EnterpriseWebAuthnConfig {
    // Enterprise security settings
    userVerification: 'required' | 'preferred' | 'discouraged'
    authenticatorAttachment: 'platform' | 'cross-platform' | undefined
    residentKey: 'required' | 'preferred' | 'discouraged'
    attestation: 'none' | 'indirect' | 'direct' | 'enterprise'

    // Timeout settings
    registrationTimeout: number
    authenticationTimeout: number

    // Enterprise features
    requireUserVerification: boolean
    supportHardwareKeys: boolean
    supportPlatformAuthenticators: boolean
}

export const ENTERPRISE_CONFIG: EnterpriseWebAuthnConfig = {
    userVerification: 'required',
    authenticatorAttachment: undefined, // undefined allows both platform (Touch ID, Face ID) and cross-platform (YubiKey)
    residentKey: 'required',
    attestation: 'enterprise',
    registrationTimeout: 60000, // 60 seconds
    authenticationTimeout: 30000, // 30 seconds
    requireUserVerification: true,
    supportHardwareKeys: true,
    supportPlatformAuthenticators: true
}

/**
 * Check if we're in a secure context (required for WebAuthn)
 * WebAuthn requires window.isSecureContext to be true, which only happens
 * when the browser trusts the certificate (not just when using HTTPS)
 */
export function isSecureContext(): boolean {
    // The most reliable check - browser's native secure context flag
    // This is only true when the certificate is trusted
    if (typeof window.isSecureContext !== 'undefined') {
        return window.isSecureContext
    }
    
    // Fallback checks (less reliable for WebAuthn)
    // These might be true even if certificate isn't trusted
    return window.location.protocol === 'https:' ||
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1'
}

/**
 * Check if WebAuthn is supported in the current browser
 */
export function isWebAuthnSupported(): boolean {
    if (!isSecureContext()) {
        return false
    }
    return !!(
        window.PublicKeyCredential &&
        window.navigator.credentials &&
        window.navigator.credentials.create &&
        window.navigator.credentials.get
    )
}

/**
 * Check if platform authenticator is available (Touch ID, Face ID, Windows Hello)
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
    if (!isWebAuthnSupported()) {
        return false
    }

    try {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        return available
    } catch {
        return false
    }
}

/**
 * Check if cross-platform authenticators are available (YubiKey, other hardware keys)
 */
export async function isCrossPlatformAuthenticatorAvailable(): Promise<boolean> {
    if (!isWebAuthnSupported()) {
        return false
    }

    try {
        // Try to create a credential with cross-platform authenticator
        const options: CredentialCreationOptions = {
            publicKey: {
                challenge: new Uint8Array(32),
                rp: { name: 'Test' },
                user: {
                    id: new TextEncoder().encode('test'),
                    name: 'test',
                    displayName: 'test'
                },
                pubKeyCredParams: [{ alg: -7, type: 'public-key' }],
                authenticatorSelection: {
                    authenticatorAttachment: 'cross-platform',
                    userVerification: 'required'
                },
                timeout: 1000
            }
        }

        // This will fail, but we can catch the error to determine support
        await navigator.credentials.create(options)
        return true
    } catch (error: any) {
        // If it's a NotSupportedError, cross-platform authenticators aren't available
        if (error.name === 'NotSupportedError') {
            return false
        }
        // Other errors (like timeout) mean the feature is supported
        return true
    }
}

/**
 * Get available authenticator types
 */
export async function getAvailableAuthenticators(): Promise<{
    platform: boolean
    crossPlatform: boolean
    yubikey: boolean
}> {
    const [platform, crossPlatform] = await Promise.all([
        isPlatformAuthenticatorAvailable(),
        isCrossPlatformAuthenticatorAvailable()
    ])

    return {
        platform,
        crossPlatform,
        yubikey: crossPlatform // YubiKey is a cross-platform authenticator
    }
}

/**
 * Register a new enterprise passkey with YubiKey support
 */
export async function registerEnterprisePasskey(
    options: WebAuthnRegisterOptions,
    deviceName?: string
): Promise<{ credential: Credential; deviceName: string }> {
    if (!isWebAuthnSupported()) {
        throw new WebAuthnNotSupportedError()
    }

    try {
        const credential = await navigator.credentials.create({
            publicKey: {
                challenge: base64UrlDecode(options.challenge),
                rp: options.rp,
                user: {
                    id: base64UrlDecode(options.user.id),
                    name: options.user.name,
                    displayName: options.user.displayName,
                },
                pubKeyCredParams: options.pubKeyCredParams,
                authenticatorSelection: {
                    // Enterprise-grade: Support both platform (Touch ID, Face ID, Windows Hello) and cross-platform (YubiKey, hardware keys)
                    // undefined allows the user to choose any available authenticator type
                    authenticatorAttachment: ENTERPRISE_CONFIG.authenticatorAttachment, // undefined = both platform and cross-platform
                    userVerification: ENTERPRISE_CONFIG.userVerification,
                    residentKey: ENTERPRISE_CONFIG.residentKey,
                    requireResidentKey: true
                },
                timeout: ENTERPRISE_CONFIG.registrationTimeout,
                attestation: 'indirect', // Match backend attestation type for proper credential extraction

                // Enterprise extensions (optional - some authenticators may not support these)
                extensions: {
                    // Credential protection extension (optional)
                    credProtect: {
                        credentialProtectionPolicy: 'userVerificationRequired',
                        enforceCredentialProtectionPolicy: false // Don't enforce - allow fallback
                    },

                    // Large blob support for enterprise data (optional)
                    largeBlob: {
                        support: 'preferred' // Changed from 'required' to 'preferred' to allow fallback
                    }
                }
            },
        })

        if (!credential) {
            throw new WebAuthnCancelledError()
        }

        // Determine device name based on authenticator type
        const finalDeviceName = deviceName || await getDeviceName(credential)

        return { credential, deviceName: finalDeviceName }
    } catch (error: any) {
        console.error('WebAuthn registration error details:', {
            name: error.name,
            message: error.message,
            stack: error.stack
        })
        if (error.name === 'NotAllowedError') {
            throw new WebAuthnCancelledError()
        }
        if (error.name === 'TimeoutError') {
            throw new WebAuthnTimeoutError()
        }
        if (error.name === 'NotSupportedError') {
            throw new Error('This authenticator is not supported. Please try a different security key or use your device\'s biometric authentication.')
        }
        throw error
    }
}

/**
 * Authenticate with an existing enterprise passkey
 */
export async function authenticateEnterprisePasskey(options: WebAuthnLoginOptions): Promise<Credential> {
    if (!isWebAuthnSupported()) {
        throw new WebAuthnNotSupportedError()
    }

    try {
        const credential = await navigator.credentials.get({
            publicKey: {
                challenge: base64UrlDecode(options.challenge),
                timeout: ENTERPRISE_CONFIG.authenticationTimeout,
                rpId: options.rpId,
                allowCredentials: options.allowCredentials?.map(cred => {
                    // Handle credential ID - backend sends it as base64url string (from @simplewebauthn/server)
                    // But it might also be a Buffer object if JSON stringified incorrectly
                    let credentialId: ArrayBuffer;
                    
                    if (typeof cred.id === 'string') {
                        // Normal case: base64url string from backend
                        credentialId = base64UrlDecode(cred.id);
                    } else if (cred.id && typeof cred.id === 'object' && 'data' in cred.id) {
                        // Buffer was JSON stringified: { type: 'Buffer', data: [...] }
                        const bufferData = new Uint8Array((cred.id as any).data);
                        credentialId = bufferData.buffer;
                    } else if (cred.id instanceof ArrayBuffer) {
                        credentialId = cred.id;
                    } else if (cred.id instanceof Uint8Array) {
                        credentialId = cred.id.buffer.slice(cred.id.byteOffset, cred.id.byteOffset + cred.id.byteLength);
                    } else {
                        throw new Error(`Invalid credential ID format: ${typeof cred.id}, value: ${JSON.stringify(cred.id)}`);
                    }
                    
                    return {
                        type: cred.type,
                        id: credentialId,
                        transports: cred.transports as AuthenticatorTransport[],
                    };
                }),
                userVerification: ENTERPRISE_CONFIG.userVerification,

                // Enterprise extensions
                extensions: {
                    // Large blob support - for authentication, we only read (don't write)
                    // write must be an ArrayBuffer/ArrayBufferView or undefined, not a boolean
                    largeBlob: {
                        read: true
                        // write is omitted for authentication - we only read existing blobs
                    }
                }
            },
        })

        if (!credential) {
            throw new WebAuthnCancelledError()
        }

        return credential
    } catch (error: any) {
        if (error.name === 'NotAllowedError') {
            throw new WebAuthnCancelledError()
        }
        if (error.name === 'TimeoutError') {
            throw new WebAuthnTimeoutError()
        }
        throw error
    }
}

/**
 * Get device name based on credential type
 */
async function getDeviceName(credential: Credential): Promise<string> {
    if (!(credential instanceof PublicKeyCredential)) {
        return 'Unknown Device'
    }

    // Check if it's a platform authenticator (Touch ID, Face ID, Windows Hello)
    if (credential.authenticatorAttachment === 'platform') {
        const userAgent = navigator.userAgent.toLowerCase()

        if (userAgent.includes('mac')) {
            return 'Touch ID / Face ID (Mac)'
        } else if (userAgent.includes('windows')) {
            return 'Windows Hello'
        } else if (userAgent.includes('android')) {
            return 'Android Biometric'
        } else if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
            return 'Touch ID / Face ID (iOS)'
        } else {
            return 'Platform Authenticator'
        }
    }

    // Check if it's a cross-platform authenticator (YubiKey, other hardware keys)
    if (credential.authenticatorAttachment === 'cross-platform') {
        // Try to detect YubiKey specifically
        if (await isYubiKey(credential)) {
            return 'YubiKey Security Key'
        } else {
            return 'Hardware Security Key'
        }
    }

    return 'Security Key'
}

/**
 * Detect if the credential is from a YubiKey
 */
async function isYubiKey(credential: PublicKeyCredential): Promise<boolean> {
    try {
        // YubiKey has specific characteristics we can detect
        const response = credential.response as AuthenticatorAttestationResponse

        if (!response.attestationObject) {
            return false
        }

        // Parse the attestation object to check for YubiKey-specific data
        const attestationObject = new Uint8Array(response.attestationObject)

        // YubiKey typically uses specific algorithms and has identifiable patterns
        // This is a simplified check - in production, you'd parse the CBOR data
        return attestationObject.length > 0 // Placeholder for actual YubiKey detection
    } catch {
        return false
    }
}

/**
 * Convert credential to format expected by server
 */
export function credentialToServerFormat(credential: Credential): any {
    if (!(credential instanceof PublicKeyCredential)) {
        throw new Error('Invalid credential type')
    }

    const response = credential.response
    if (!response) {
        throw new Error('No response from credential')
    }

    if (response instanceof AuthenticatorAttestationResponse) {
        // Registration response
        return {
            id: credential.id,
            rawId: base64UrlEncode(credential.rawId),
            type: credential.type,
            response: {
                clientDataJSON: base64UrlEncode(response.clientDataJSON),
                attestationObject: base64UrlEncode(response.attestationObject),
                transports: credential.response.getTransports?.() || ['usb', 'nfc', 'ble']
            },
        }
    } else if (response instanceof AuthenticatorAssertionResponse) {
        // Authentication response
        return {
            id: credential.id,
            rawId: base64UrlEncode(credential.rawId),
            type: credential.type,
            response: {
                clientDataJSON: base64UrlEncode(response.clientDataJSON),
                authenticatorData: base64UrlEncode(response.authenticatorData),
                signature: base64UrlEncode(response.signature),
                userHandle: response.userHandle ? base64UrlEncode(response.userHandle) : null,
            },
        }
    }

    throw new Error('Unknown response type')
}

/**
 * Get user-friendly error message
 */
export function getWebAuthnErrorMessage(error: Error): string {
    if (error instanceof WebAuthnNotSupportedError) {
        return 'Your browser does not support passkeys. Please use a modern browser like Chrome, Firefox, Safari, or Edge.'
    }

    if (error instanceof WebAuthnCancelledError) {
        return 'Passkey operation was cancelled. Please try again.'
    }

    if (error instanceof WebAuthnTimeoutError) {
        return 'Passkey operation timed out. Please try again.'
    }

    if (error.name === 'NotAllowedError') {
        return 'Passkey operation was not allowed. Please ensure your security key is connected and try again.'
    }

    if (error.name === 'NotSupportedError') {
        return 'Your device does not support this type of passkey. Please try a different security key.'
    }

    if (error.name === 'SecurityError') {
        const isSecure = isSecureContext()
        if (!isSecure) {
            return 'WebAuthn requires a secure connection (HTTPS). Please access this page via HTTPS or localhost. If using a self-signed certificate, you may need to accept it in your browser first.'
        }
        // If secure context is true but we still get SecurityError, it's likely an RP_ID or origin mismatch
        return `Security error: ${error.message || 'RP_ID or origin mismatch. Please ensure the certificate matches the hostname (${window.location.hostname}).'}`
    }

    return `Passkey error: ${error.message}`
}

/**
 * Base64 URL encode
 */
function base64UrlEncode(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i])
    }
    return btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '')
}

/**
 * Base64 URL decode
 */
function base64UrlDecode(input: string | ArrayBuffer | Uint8Array): ArrayBuffer {
    // Handle different input types
    let str: string;
    if (typeof input === 'string') {
        str = input;
    } else if (input instanceof ArrayBuffer) {
        // Already an ArrayBuffer, return as-is
        return input;
    } else if (input instanceof Uint8Array) {
        // Convert Uint8Array to ArrayBuffer
        return input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength);
    } else {
        throw new Error(`Invalid input type for base64UrlDecode: ${typeof input}`);
    }
    
    // Add padding if needed
    str += '='.repeat((4 - (str.length % 4)) % 4)
    str = str.replace(/-/g, '+').replace(/_/g, '/')

    const binary = atob(str)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i)
    }
    return bytes.buffer
}

/**
 * Enterprise WebAuthn API client
 */
export class EnterpriseWebAuthnAPI {
    private baseURL: string

    constructor(baseURL?: string) {
        if (baseURL) {
            this.baseURL = baseURL
            return
        }

        const envBase = import.meta.env.VITE_API_BASE_URL as string | undefined
        if (envBase) {
            const normalized = envBase.endsWith('/')
                ? envBase.slice(0, -1)
                : envBase
            this.baseURL = `${normalized}/api`
        } else {
            this.baseURL = '/api'
        }
    }

    /**
     * Begin enterprise passkey registration
     */
    async beginRegistration(email?: string): Promise<WebAuthnRegisterOptions> {
        const response = await fetch(`${this.baseURL}/webauthn/enterprise/register/begin`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: email ? JSON.stringify({ email }) : undefined,
        })

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: response.statusText }))
            throw new Error(error.message || `Registration begin failed: ${response.statusText}`)
        }

        const result = await response.json()
        return result.data || result
    }

    /**
     * Complete enterprise passkey registration
     */
    async completeRegistration(credential: Credential, deviceName?: string, email?: string): Promise<any> {
        const credentialData = credentialToServerFormat(credential)

        const response = await fetch(`${this.baseURL}/webauthn/enterprise/register/complete`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                credential: credentialData,
                deviceName,
                email
            }),
        })

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: response.statusText }))
            throw new Error(error.message || `Registration complete failed: ${response.statusText}`)
        }

        const result = await response.json()
        return result
    }

    /**
     * Begin enterprise passkey authentication
     */
    async beginAuthentication(email: string): Promise<WebAuthnLoginOptions> {
        const response = await fetch(`${this.baseURL}/webauthn/enterprise/authenticate/begin`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
        })

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: response.statusText }))
            // Extract error message from response format: { success: false, message: "...", error: "..." }
            const errorMessage = error.message || error.error || `Authentication begin failed: ${response.statusText}`
            console.error('WebAuthn authentication begin failed', {
                status: response.status,
                statusText: response.statusText,
                error: error,
                errorMessage
            })
            throw new Error(errorMessage)
        }

        const result = await response.json()
        console.log('WebAuthn authentication begin response', {
            hasData: !!result.data,
            hasResult: !!result,
            resultKeys: Object.keys(result),
            allowCredentialsCount: result.data?.allowCredentials?.length || result.allowCredentials?.length || 0
        })
        
        // Handle response format: { success: true, data: {...} }
        const options = result.data || result
        
        // Validate and log the structure
        if (options.allowCredentials) {
            console.log('allowCredentials structure', {
                count: options.allowCredentials.length,
                firstCredential: options.allowCredentials[0] ? {
                    hasId: !!options.allowCredentials[0].id,
                    idType: typeof options.allowCredentials[0].id,
                    idValue: typeof options.allowCredentials[0].id === 'string' 
                        ? options.allowCredentials[0].id.substring(0, 20) + '...'
                        : options.allowCredentials[0].id
                } : null
            })
        }
        
        return options
    }

    /**
     * Complete enterprise passkey authentication
     */
    async completeAuthentication(credential: Credential, email: string): Promise<any> {
        const credentialData = credentialToServerFormat(credential)

        const response = await fetch(`${this.baseURL}/webauthn/enterprise/authenticate/complete`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                credential: credentialData,
                email
            }),
        })

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: response.statusText }))
            throw new Error(error.message || `Authentication complete failed: ${response.statusText}`)
        }

        const result = await response.json()
        // Handle response format: { success: true, data: {...} }
        return result
    }

    /**
     * List enterprise credentials
     */
    async listCredentials(): Promise<any> {
        const response = await fetch(`${this.baseURL}/webauthn/enterprise/credentials`, {
            method: 'GET',
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(`List credentials failed: ${response.statusText}`)
        }

        return response.json()
    }

    /**
     * Remove enterprise credential
     */
    async removeCredential(credentialId: string): Promise<any> {
        const response = await fetch(`${this.baseURL}/webauthn/enterprise/credentials/${credentialId}`, {
            method: 'DELETE',
            credentials: 'include',
        })

        if (!response.ok) {
            throw new Error(`Remove credential failed: ${response.statusText}`)
        }

        return response.json()
    }
}
